import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: "name" };

function canViewDeliverySites(url, permissions) {
    const isCustomerNested =
        url && url.includes("/api/customers/") && url.includes("/deliverysites");
    const requiredPermission = isCustomerNested
        ? "core.view_customer"
        : "core.view_deliverysite";
    return permissions.indexOf(requiredPermission) !== -1;
}

const createState = (options = {}) => ({
    deliverysites: [],
    deliverysites_url: '/api/deliverysites/',
    deliverysites_filters: DEFAULT_FILTERS,
    deliverysites_count: 0,
    deliverysites_loaded: false,
    deliverysites_loading: false,
    deliverysites_loading_errors: null,
    cancelTokenSource: null,
    _storageOptions: options || {},
});

const mutations = {
    updateDeliverySites(state, deliverysites) {
        state.deliverysites = deliverysites;
        state.deliverysites_loaded = true;
        state.deliverysites_loading = false;
        state.deliverysites_loading_errors = null;
    },
    updateDeliverySitesCount(state, deliverysites_count) {
        state.deliverysites_count = deliverysites_count;
    },

    updateDeliverySitesUrl(state, deliverysites_url) {
        state.deliverysites_url = deliverysites_url;
    },
    updateDeliverySitesLoading(state, deliverysites_loading) {
        state.deliverysites_loading = deliverysites_loading;
    },
    updateDeliverySitesLoadingErrors(state, deliverysites_loading_errors) {
        state.deliverysites_loading_errors = deliverysites_loading_errors;
        state.deliverysites_loading = false;
    },
    updateDeliverySitesFilters(state, filters) {
        state.deliverysites_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.deliverysites_filters, fieldsToSave);
        }
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {

    async deleteDeliverySite({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`${state.deliverysites_url}${params.instance.id}/archive/`);
            dispatch('fetchDeliverySites');
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async setDefaultDeliverySite({ dispatch, state }, params) {
        try {
            const response = await axios.post(`${state.deliverysites_url}${params.instance.id}/default/`);
            dispatch('fetchDeliverySites');
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async fetchDeliverySites({ commit, dispatch, state }, params) {

        if (
            !canViewDeliverySites(
                state.deliverysites_url,
                store.getters["session/current_user_permissions"]
            )
        ) {
            commit("updateDeliverySites", []);
            commit("updateDeliverySitesCount", 0);
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateDeliverySitesLoading', true);
        commit('updateDeliverySitesLoadingErrors', null);

        try {
            const response = await axios.get(state.deliverysites_url, {
                params: state.deliverysites_filters,
                cancelToken: source.token,
            });
            commit('updateDeliverySites', response.data.results);
            commit('updateDeliverySitesCount', response.data.count);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateDeliverySitesLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateDeliverySitesLoading', false);
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateDeliverySitesUrl', params?.url || '/api/deliverysites/');
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateDeliverySitesFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
        
        commit('updateDeliverySites', []);
    }
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});